import api from './api.service';
import type {
  Lease,
  LeaseCancelRequest,
  LeaseExternalSignedFinalizeRequest,
} from '../types/lease';

class LeasesService {
  async getById(leaseId: string): Promise<Lease> {
    const response = await api.get(`/leases/${leaseId}`);
    return response.data;
  }

  /**
   * Read the Lease attached to a candidate. Returns null when the API
   * returns 404 (e.g. candidate still PENDING — no Lease created yet).
   */
  async getByCandidate(candidateId: string): Promise<Lease | null> {
    try {
      const response = await api.get(`/leases`, {
        params: { candidate_id: candidateId },
      });
      return response.data;
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) return null;
      throw err;
    }
  }

  /**
   * Upload the externally-signed lease PDF (multipart) and finalize the
   * Lease. Triggers the post-signature cascade server-side.
   */
  async uploadExternalSigned(
    leaseId: string,
    file: File,
    payload: LeaseExternalSignedFinalizeRequest,
  ): Promise<Lease> {
    const form = new FormData();
    form.append('file', file);
    form.append('payload', JSON.stringify(payload));
    // The global axios instance defaults to 'Content-Type: application/json'.
    // For multipart we must unset it so the browser can build the right
    // 'multipart/form-data; boundary=...' header from the FormData instance.
    // Casting headers to `any` avoids a TS narrowing complaint on the
    // `undefined` literal — axios accepts it at runtime and that's what we
    // want here (let the browser/FormData layer set the boundary).
    const response = await api.post(
      `/leases/${leaseId}/external-signed-document`,
      form,
      { headers: { 'Content-Type': undefined } as any },
    );
    return response.data;
  }

  async cancel(leaseId: string, data: LeaseCancelRequest = {}): Promise<Lease> {
    const response = await api.post(`/leases/${leaseId}/cancel`, data);
    return response.data;
  }
}

export const leasesService = new LeasesService();
